import React from 'react';
import styled, { css, keyframes } from 'styled-components';

export type CanvasAspect = '9:16' | '16:9' | '1:1' | '4:5';
export type CanvasState = 'empty' | 'generating' | 'ready' | 'error';

export type StudioCanvasProps = {
  aspect?: CanvasAspect;
  state?: CanvasState;
  emptyTitle?: string;
  emptyHint?: string;
  errorTitle?: string;
  errorHint?: string;
  progressLabel?: string;
  progressPercent?: number;
  toolbar?: React.ReactNode;
  footer?: React.ReactNode;
  children?: React.ReactNode;
  maxHeight?: string;
  className?: string;
};

const aspectRatio: Record<CanvasAspect, string> = {
  '9:16': '9 / 16',
  '16:9': '16 / 9',
  '1:1': '1 / 1',
  '4:5': '4 / 5',
};

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.6; }
  50% { opacity: 1; }
`;

const Root = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-3);
  width: 100%;
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
  width: 100%;
  max-width: 720px;
`;

type FrameProps = {
  $aspect: CanvasAspect;
  $state: CanvasState;
  $maxHeight: string;
};

const Frame = styled.div<FrameProps>`
  position: relative;
  width: 100%;
  max-width: 720px;
  max-height: ${(p) => p.$maxHeight};
  aspect-ratio: ${(p) => aspectRatio[p.$aspect]};
  border-radius: var(--radius-xl);
  overflow: hidden;
  background: var(--color-surface-inset);
  border: 1px solid var(--color-border-subtle);
  box-shadow: var(--shadow-md);
  display: flex;
  align-items: center;
  justify-content: center;

  ${(p) =>
    p.$state === 'generating' &&
    css`
      &::after {
        content: '';
        position: absolute;
        inset: 0;
        background: linear-gradient(
          90deg,
          transparent 0%,
          rgba(255, 255, 255, 0.14) 50%,
          transparent 100%
        );
        background-size: 200% 100%;
        animation: ${shimmer} 1.6s ease-in-out infinite;
        pointer-events: none;
      }
    `}
`;

const Content = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  & > img,
  & > video {
    width: 100%;
    height: 100%;
    object-fit: contain;
    background: #000;
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  text-align: center;
  padding: var(--space-6);
  color: var(--color-text-secondary);
`;

const EmptyMark = styled.div`
  width: 56px;
  height: 56px;
  border-radius: var(--radius-full);
  background: linear-gradient(
    135deg,
    var(--color-ai-gradient-from),
    var(--color-ai-gradient-to)
  );
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  animation: ${pulse} 2.4s ease-in-out infinite;
`;

const EmptyTitle = styled.div`
  font-family: var(--font-display);
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
`;

const EmptyHint = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  max-width: 36ch;
  line-height: var(--line-height-base);
`;

const ProgressOverlay = styled.div`
  position: absolute;
  left: 50%;
  bottom: var(--space-4);
  transform: translateX(-50%);
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-3);
  background: rgba(0, 0, 0, 0.6);
  color: #fff;
  border-radius: var(--radius-pill);
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
`;

const ProgressDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: var(--radius-full);
  background: var(--color-accent);
  animation: ${pulse} 1.2s ease-in-out infinite;
`;

const ProgressBar = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 3px;
  background: rgba(255, 255, 255, 0.12);
`;

const ProgressFill = styled.div<{ $percent: number }>`
  height: 100%;
  width: ${(p) => Math.max(0, Math.min(100, p.$percent))}%;
  background: var(--color-accent);
  transition: width var(--duration-slow) var(--ease-standard);
`;

const FooterRow = styled.div`
  width: 100%;
  max-width: 720px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-3);
`;

export const StudioCanvas: React.FC<StudioCanvasProps> = ({
  aspect = '9:16',
  state = 'empty',
  emptyTitle = 'Nothing here yet',
  emptyHint = 'Write a prompt and hit Generate to see your first variant.',
  errorTitle = 'Generation failed',
  errorHint = 'Try regenerating, or tweak the prompt and run again.',
  progressLabel,
  progressPercent,
  toolbar,
  footer,
  children,
  maxHeight = '70vh',
  className,
}) => {
  const showEmpty = state === 'empty';
  const showError = state === 'error';
  const showProgress =
    state === 'generating' && (progressLabel !== undefined || progressPercent !== undefined);

  return (
    <Root className={className}>
      {toolbar && <Toolbar>{toolbar}</Toolbar>}

      <Frame $aspect={aspect} $state={state} $maxHeight={maxHeight}>
        {!showEmpty && !showError && <Content>{children}</Content>}

        {showEmpty && (
          <EmptyState>
            <EmptyMark>✨</EmptyMark>
            <EmptyTitle>{emptyTitle}</EmptyTitle>
            <EmptyHint>{emptyHint}</EmptyHint>
          </EmptyState>
        )}

        {showError && (
          <EmptyState>
            <EmptyMark style={{ background: 'var(--color-danger-subtle)' }}>⚠️</EmptyMark>
            <EmptyTitle>{errorTitle}</EmptyTitle>
            <EmptyHint>{errorHint}</EmptyHint>
          </EmptyState>
        )}

        {showProgress && (
          <ProgressOverlay>
            <ProgressDot />
            {progressLabel ?? `${Math.round(progressPercent ?? 0)}%`}
          </ProgressOverlay>
        )}

        {state === 'generating' && progressPercent !== undefined && (
          <ProgressBar>
            <ProgressFill $percent={progressPercent} />
          </ProgressBar>
        )}
      </Frame>

      {footer && <FooterRow>{footer}</FooterRow>}
    </Root>
  );
};

StudioCanvas.displayName = 'StudioCanvas';
